"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFormErrors = void 0;
const react_1 = require("react");
const useLocalContext_1 = require("./useLocalContext");
const useProxy_1 = require("./useProxy");
const utils_1 = require("./utils");
function useFormErrors(args) {
    const { formContext, onError = utils_1.identity, validator, noPositive, name, } = args;
    const [errors, setErrors] = (0, react_1.useState)({});
    const [externalErrors, setExternalErrors] = (0, react_1.useState)({});
    const [positive, setPositive] = (0, react_1.useState)({});
    const [touched, setTouched] = (0, react_1.useState)({});
    const [validating, setValidating] = (0, react_1.useState)({});
    const ctx = (0, useLocalContext_1.useLocalContext)({ errors, externalErrors, positive, touched, validating });
    const errorsProxy = new Proxy(errors, {
        get(target, name) {
            if (name === '__form') {
                return target[name];
            }
            return touched[name] ? target[name] : undefined;
        },
    });
    function updateExternalErrors(newErrors) {
        if (newErrors) {
            updateTouched(newErrors);
        }
        setExternalErrors(newErrors);
    }
    const updateExternalErrorsProxy = (0, useProxy_1.useProxy)(updateExternalErrors, name => error => updateExternalErrors({ [name]: error }));
    function updateErrors(changedErrors = {}) {
        const newErrors = (0, utils_1.stripEmptyValues)(Object.assign(Object.assign({}, ctx.errors), changedErrors));
        setErrors(newErrors);
        onError(Object.keys(newErrors).length > 0 ? newErrors : null, formContext.values);
        setPositive(updatePositive(formContext.values, newErrors));
    }
    function updateValidating(changedValidating) {
        setValidating(Object.assign(Object.assign({}, validating), changedValidating));
    }
    const updateValidatingProxy = (0, useProxy_1.useProxy)(updateValidating, name => validating => updateValidating({ [name]: validating }));
    function validateValues(values) {
        updateErrors(updateValidationErrors(validator, values, ctx.externalErrors, updateTouched));
    }
    function updateTouched(changedTouched) {
        setTouched(Object.assign(Object.assign({}, ctx.touched), (0, utils_1.replaceValues)(changedTouched, true)));
    }
    const handleBlurProxy = (0, useProxy_1.useProxy)({}, name => () => {
        updateTouched({ [name]: true });
        return validateValues(formContext.values);
    });
    (0, react_1.useEffect)(() => {
        validateValues(formContext.values);
    }, [formContext.values, ctx.externalErrors]);
    return {
        errors: errorsProxy,
        updateErrors: updateExternalErrorsProxy,
        validating: ctx.validating,
        updateValidating: updateValidatingProxy,
        touched: ctx.touched,
        positive: ctx.positive,
        handleBlur: handleBlurProxy,
        hasErrors: !!buildErrorList(errors),
        hasFieldErrors: !!buildErrorList(errors, true),
        anyValidating: Object.values(ctx.validating).some(x => !!x),
        validateValues,
        setErrors,
    };
}
exports.useFormErrors = useFormErrors;
function updatePositive(values, errors) {
    const newPositive = {};
    for (const [key, value] of Object.entries(values)) {
        newPositive[key] = !errors[key] && !(0, utils_1.isEmpty)(value);
    }
    return newPositive;
}
function updateValidationErrors(validator, values, externalErrors, onTouched) {
    let newErrors = {};
    if (validator) {
        newErrors = (0, utils_1.replaceValues)(validator.fields, null);
        try {
            validator.validateSync(values, {
                abortEarly: false,
                context: {
                    onTouched,
                }
            });
        }
        catch (e) {
            if (e.name === 'ValidationError') {
                newErrors = e.inner.reduce((a, b) => {
                    return Object.assign(Object.assign({}, a), { [b.path]: b.message });
                }, newErrors);
            }
            else {
                throw e;
            }
        }
    }
    return Object.assign(Object.assign({}, newErrors), externalErrors);
}
function buildErrorList(errors, skipFormErrors = false) {
    const errorList = [];
    for (const [key, value] of Object.entries(errors)) {
        if ((0, utils_1.isEmpty)(value)) {
            continue;
        }
        if (key === '__form' && skipFormErrors) {
            continue;
        }
        errorList.push(value);
    }
    return errorList.length ? errorList : null;
}
//# sourceMappingURL=useFormErrors.js.map